/**
 * Role Modal Component
 *
 * Modal for creating or editing a role.
 * Features: form validation, name and description fields.
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconShield } from '@tabler/icons-react';
import { Modal, Stack, Button, Group, Alert, TextInput, Textarea } from '../../../../components/ui';
import { usePermissionManagement } from '../hooks/usePermissionManagement';
import type { RoleModalProps } from '../types';

export function RoleModal({ opened, onClose, onSuccess, role }: RoleModalProps) {
  const permissionMgmt = usePermissionManagement();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEditMode = !!role;

  // Form setup with validation
  const form = useForm({
    initialValues: {
      name: role?.name || '',
      description: role?.description || '',
    },
    validate: {
      name: (value) => {
        if (!value || value.trim() === '') return 'Role name is required';
        if (value.trim().length < 2) return 'Role name must be at least 2 characters';
        if (value.trim().length > 50) return 'Role name must not exceed 50 characters';
        return null;
      },
      description: (value) => {
        if (value && value.trim().length > 500) {
          return 'Description must not exceed 500 characters';
        }
        return null;
      },
    },
  });

  // Update form when role prop changes
  useEffect(() => {
    if (role) {
      form.setValues({
        name: role.name || '',
        description: role.description || '',
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: typeof form.values) => {
    setSubmitError(null);

    try {
      if (isEditMode) {
        // Update existing role
        await permissionMgmt.updateRole(role.id, {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
        });
      } else {
        // Create new role
        await permissionMgmt.createRole({
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
        });
      }

      form.reset();
      onSuccess();
    } catch (err: any) {
      setSubmitError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} role`);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    form.reset();
    setSubmitError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconShield size={20} />
          {isEditMode ? 'Edit Role' : 'Create New Role'}
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Error Alert */}
          {submitError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          {/* Role Name */}
          <TextInput
            label="Role Name"
            placeholder="e.g., Admin, Manager, User"
            required
            {...form.getInputProps('name')}
          />

          {/* Description */}
          <Textarea
            label="Description"
            placeholder="Describe the purpose and responsibilities of this role"
            rows={4}
            {...form.getInputProps('description')}
          />

          {/* Action Buttons */}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={permissionMgmt.loading} variant="gradient" gradientVariant="primary">
              {isEditMode ? 'Update Role' : 'Create Role'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
